/**
 * CLIENT RPC CATALOGUE — the one place the app names a Postgres function.
 *
 * WHY THIS EXISTS
 * ---------------
 * Postgres publishes every `public` function as a PostgREST endpoint, and
 * `20260729120000_rpc_privilege_lockdown.sql` made exposure deny-by-default: a
 * function is callable by `anon`/`authenticated` only if something explicitly
 * grants it. Nothing connected that grant list to the code that actually calls
 * these functions, so two RPCs shipped to production with no grant at all and
 * failed with 42501 for ten days:
 *
 *   • `get_usage_summary`   — usage counters silently read as zero
 *   • `check_waitlist_status` — EVERY beta signup refused as "not invited"
 *
 * Both were invisible to a `.rpc('name')` grep because their call sites went
 * through `(supabase.rpc as any)(…)`, which puts the function name on a
 * different line from the `.rpc` token. A guard that can be defeated by a cast
 * is not a guard — so the name list lives here, in data, and ARCH-17 keeps
 * `.rpc` out of every other file.
 *
 * CONSEQUENCES
 * ------------
 *   • `CLIENT_RPCS` is the machine-readable contract CI checks against the live
 *     database (`scripts/ci/check-client-rpc-grants.ts`): every name here must
 *     be executable by the role that calls it, or the deploy fails.
 *   • Every entry must have a matching row in the `public.rpc_exposure`
 *     registry (see `20260808120000_rpc_exposure_registry.sql`), which is what
 *     actually issues the GRANT.
 *
 * ADDING AN RPC: add the name below, add its `rpc_exposure` row in the same
 * migration that creates the function, and call it through `rpc()`.
 */

import type { Database } from './database.types';
import { supabase } from './client';

type Functions = Database['public']['Functions'];

/**
 * Every Postgres function this app calls from the client.
 *
 * `anon` marks the two that run BEFORE authentication — they are reachable by
 * anyone holding the public key, so each needs a standalone justification in the
 * registry rather than the ordinary "asserts auth.uid()" argument.
 */
export const CLIENT_RPCS = [
  'check_waitlist_status', // anon — invite-only signup pre-check, runs pre-auth
  'closed_company_directory', // 119-07: the directory's closed companies, card fields only
  'company_public_card', // 119-07: a SEALED company's public card (status + numeric score history)
  'conclude_dd_report',
  'dd_report_data_room_pass', // owner rule 2026-10-23: is this DD report backed by the data room?
  'enabled_features',
  'get_data_room_view_stats',
  'get_investor_engagement_metrics',
  'get_investor_reviews',
  'get_related_investors',
  'get_shared_dd_reports',
  'get_usage_summary',
  'get_waitlist_stats',
  'has_data_room_access', // THE data-room pass; the founder decides who is told about a new document
  'hybrid_search_documents',
  'investor_score_events', // the ONLY client read of score_event for non-members (projection)
  'investor_startup_have_engaged',
  'match_conversation_history',
  'match_document_chunks',
  'read_marks_for_conversation',
  'respond_to_data_room_request',
  'update_my_function_title',
  'withdraw_data_room_request',
] as const satisfies readonly (keyof Functions)[];

export type ClientRpcName = (typeof CLIENT_RPCS)[number];

/**
 * Call a catalogued Postgres function.
 *
 * This is an EXACT passthrough to `supabase.rpc` — identical argument and
 * return typing, resolved per function name from the generated schema. That is
 * deliberate: wrapping it in a generic helper collapses the return type into a
 * union of every catalogued function's `Returns`, which would push callers
 * straight back to the `as any` casts this module exists to delete.
 *
 * The name restriction is therefore enforced by CI rather than by the type
 * system: ARCH-17 keeps `.rpc` out of every other file, and
 * `scripts/ci/check-client-rpc-grants.ts` fails when a name reached through
 * `rpc(...)` is missing from `CLIENT_RPCS` or lacks a grant in the database.
 * A grep is reliable here precisely because the casts are gone.
 *
 * Returns supabase-js's `{ data, error }` — it does NOT throw. Bind and check
 * `error`; a failed read is not an empty read (see the usage-store regression
 * guard for what happens when that distinction is dropped).
 */
// The implementation is deliberately untyped and the EXPORTED type is exact.
// Typing the body instead makes TS try to reconcile supabase-js's per-function
// generics against a generic forwarder, which it cannot do (TS2589, "type
// instantiation is excessively deep") — so the one cast lives here, contained,
// and every call site keeps full inference.
//
// Resolved at CALL time, not import time: binding eagerly would make merely
// importing this module depend on the client already being constructed.
const forward = (...args: unknown[]): unknown =>
  (supabase.rpc as unknown as (...a: unknown[]) => unknown)(...args);

export const rpc = forward as unknown as typeof supabase.rpc;
